import { computed, effect, inject, Injectable, signal } from '@angular/core';

import { UsersApi } from '../api/users.api';
import { AuthService } from '../auth/auth.service';
import { isProfileComplete, type MeUser, type UpdateProfilePayload } from '../auth/auth.types';

/**
 * Caches the Growzz-side user profile (`GET /users/me`) so guards, the home
 * greeting, and the onboarding wizard all read from the same signal.
 *
 * Distinct from `AuthService` which owns Auth0 session state. This service
 * reacts to `auth.isAuthenticated()` flipping to `false` (e.g. logout) and
 * clears its cache automatically.
 */
@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly users = inject(UsersApi);
  private readonly auth = inject(AuthService);

  private readonly _profile = signal<MeUser | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  readonly profile = this._profile.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  readonly firstName = computed(() => this._profile()?.firstName ?? null);
  readonly isProfileComplete = computed(() => isProfileComplete(this._profile()));

  private inFlight: Promise<MeUser> | null = null;

  constructor() {
    effect(() => {
      if (!this.auth.isAuthenticated()) {
        this.clear();
      }
    });
  }

  /**
   * Returns the cached profile if loaded; otherwise fetches once. Concurrent
   * callers share the same in-flight request.
   */
  async ensureLoaded(): Promise<MeUser | null> {
    const cached = this._profile();
    if (cached) return cached;
    if (this.inFlight) return this.inFlight;
    return this.refresh();
  }

  /**
   * Forces a fresh `GET /users/me`. Use this after a profile update or after
   * the Auth0 callback so the cache reflects server state.
   */
  async refresh(): Promise<MeUser> {
    if (this.inFlight) return this.inFlight;
    this._loading.set(true);
    this._error.set(null);
    this.inFlight = this.users
      .me({ expand: ['country'] })
      .then((user) => {
        this._profile.set(user);
        return user;
      })
      .catch((err) => {
        this._error.set('We could not load your profile. Please try again.');
        throw err;
      })
      .finally(() => {
        this._loading.set(false);
        this.inFlight = null;
      });
    return this.inFlight;
  }

  /**
   * `me()` with retry — used right after the Auth0 callback while the API
   * provisions the user row. Mirrors `UsersApi.meWithRetry()`.
   */
  async refreshWithRetry(maxAttempts = 5, delayMs = 1_000): Promise<MeUser> {
    this._loading.set(true);
    this._error.set(null);
    try {
      const user = await this.users.meWithRetry(maxAttempts, delayMs);
      this._profile.set(user);
      return user;
    } catch (err) {
      this._error.set('We could not load your profile. Please try again.');
      throw err;
    } finally {
      this._loading.set(false);
    }
  }

  /** PATCH `/users/me` and refresh the cached profile. */
  async update(patch: UpdateProfilePayload): Promise<MeUser> {
    const updated = await this.users.update(patch);
    this._profile.set(updated);
    return updated;
  }

  clear(): void {
    this._profile.set(null);
    this._error.set(null);
    this._loading.set(false);
  }
}
